import React, { useState, useEffect, useRef } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Purchase from './Purchase';
import viewAnalytics from './viewAnalytics';
import Top from '../Top/Top';
import TemplateParser from '../Templates/TemplateParser';
import ProductItem from '../Category/ProductItem';
import Slider from '../../hocs/Slider/Slider';
import Breadcrumbs from '../Category/NewBreadcrumbs';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import LazyImage from '../../hocs/Lazy/LazyImage';
import Pagination from '../../hocs/Pagination/Pagination';
import VideoPlayer from '../../hocs/HLSVideo/HLSVideo';
import { resetFilter } from '../../actions/environment';
import { addClass, removeClass, purifier, addScript, removeScript } from '../../utils/dom';
import { imageSizeTranslation } from '../../utils/image';
import { deepClone } from '../../utils/object';
import { translate, prefix, translateObjectData } from '../../utils/locale';
import { blackOrWhite, centraWysiwyg, rootUrl, wordCount } from '../../utils/string';
import { origin } from '../../config';
import s from './Product.scss';

const Product = ({ data, currentPath, ignoreSizeRelations, setup, productCache, listItemCache, filter, pageData, ...props }) => {
	if (!data || !data.productData || !productCache?.[setup.market]?.[setup.pricelist]) return null;
	const { products: cache } = productCache[setup.market][setup.pricelist];
	const [currentVariant, switchVariant] = useState(currentPath);

	let selectedVariant = '';
	if (currentVariant && pageData?.[currentVariant]?.productData) selectedVariant = currentVariant;
	const currentProduct = selectedVariant ? pageData[selectedVariant].productData : data.productData;
	const product = translateObjectData(setup, currentProduct);

	const [analytics, analyticsShown] = useState(false);

	const price = product?.markets?.[setup.market] ? product.markets[setup.market].pricesByPricelist[setup.pricelist] : {};
	const descriptionParts = product.prd_short_description ? centraWysiwyg(product.prd_short_description).split('<strong>') : product.description.split('<strong>');
	const [descriptionIntro] = wordCount(descriptionParts[0], 11);
	const sizes = Object.keys(product.items).map((item) => ({ name: product.items[item].name, item, stock: product.items[item].stockByMarket[setup.market] }));
	const shareLocation = `${origin}${prefix(setup, product.canonicalUri)}`;
	const productSections = data.parts || [];
	const productRelations = {};
	let soldOut = product?.markets?.[setup.market] ? product.markets[setup.market].stockOfAllItems === 0 : 0;
	let newSet = false;
	let saleSet = false;
	let salePercent = '0%';
	if (price.showAsOnSale) {
		salePercent = `${Math.round(price.discountPercent || ((price.priceReductionAsNumber / price.priceBeforeDiscountAsNumber) * 100))}%`;
	}
	// const inPageMenu = productSections.filter((section) => section?.sectionTitle?.showInMenu && section?.sectionTitle?.text);

	useEffect(() => {
		if (analytics !== currentVariant) {
			viewAnalytics(price, product, setup, listItemCache);
			analyticsShown(currentVariant);
		}
	}, [currentVariant, product, price, setup, analytics]);

	useEffect(() => {
		switchVariant(currentPath);
	}, [currentPath]);

	const toggleSoundHandler = (e) => {
		e.preventDefault();
		const $video = e.currentTarget.querySelector('video');
		$video.muted = !$video.muted;
		if ($video.muted) {
			removeClass(e.currentTarget, 'u_soundOn');
		} else {
			addClass(e.currentTarget, 'u_soundOn');
		}
	};

	if (product?.relatedProducts) {
		product.relatedProducts.forEach((rP) => {
			if (!productCache?.[setup.market]?.[setup.pricelist]?.products?.[rP.product]) return;
			if (ignoreSizeRelations && rP.relation === 'size') return;
			if (!productRelations[rP.relation]) productRelations[rP.relation] = [];
			productRelations[rP.relation] = [...productRelations[rP.relation], rP.product];
		});
	}
	if (productRelations?.size?.length && soldOut) soldOut = productRelations.size.every((rP) => cache?.[rP]?.inStock === 0);

	const metaInfo = {
		title: `${product.name}`,
		metaDescription: product.metaDescription,
		linkCanonical: shareLocation,
	};


	let badges = [];
	const isReSkate = product.canonicalUri.indexOf('re-skate') !== -1;
	if (product.badges) {
		badges = Object.keys(product.badges)
			.filter((b) => {
				if (parseInt(product.badges[b].is_new, 10) && !price.newProduct) return false;
				if ((parseInt(product.badges[b].is_sale, 10) && !price.showAsOnSale) || isReSkate) return false;
				if (product.badges[b].incPricelist && product.badges[b].incPricelist.split(/\s*,\s*/).indexOf(setup.pricelist) === -1) return false;
				if (product.badges[b].incMarket && product.badges[b].incMarket.split(/\s*,\s*/).indexOf(String(setup.market)) === -1) return false;
				if (product.badges[b].incCountry && product.badges[b].incCountry.split(/\s*,\s*/).indexOf(setup.country) === -1) return false;
				if (!product.badges[b]?.image) return false;
				return true;
			})
			.map((badge) => {
				if (parseInt(product.badges[badge].is_new, 10) && price.newProduct) newSet = true;
				if (parseInt(product.badges[badge].is_sale, 10) && price.showAsOnSale) saleSet = true;

				return (
					<span key={badge} className={[s.productDetail_badge, s.productDetail_badge__svg].join(' ')}>
						<img src={product.badges[badge].image.url} alt="" />
					</span>
				);
			});
	} else if (price.newProduct) {
		badges.push(<span key="new-badge" className={[s.productDetail_badge].join(' ')}>{ translate(setup, 'new') }</span>);
	}

	let tags = [];
	if (product.product_tags) {
		tags = Object.keys(product.product_tags).filter((t) => {
			if (parseInt(product.product_tags[t].is_new, 10) && !price.newProduct) return false;
			if (parseInt(product.product_tags[t].is_sale, 10) && !price.showAsOnSale) return false;
			if (product.product_tags[t].incPricelist && product.product_tags[t].incPricelist.split(/\s*,\s*/).indexOf(setup.pricelist) === -1) return false;
			if (product.product_tags[t].incMarket && product.product_tags[t].incMarket.split(/\s*,\s*/).indexOf(setup.market) === -1) return false;
			if (product.product_tags[t].incCountry && product.product_tags[t].incCountry.split(/\s*,\s*/).indexOf(setup.country) === -1) return false;
			return true;
		}).map((t) => {
			const tagParts = product.product_tags[t].tag.split(' ');
			if (tagParts.indexOf('%') !== -1 || tagParts.indexOf('-%') !== -1) {
				saleSet = true;
				return <span key={`tag-${t}`} className={s.productImages_tag} style={{ backgroundColor: product.product_tags[t].listingColor || '#FFF', color: blackOrWhite(product.product_tags[t].listingColor || '#FFF', 1.05) }}>{ product.product_tags[t].tag.replace(/(-)?%/, `$1${salePercent}`) }</span>;
			}
			return <span key={`tag-${t}`} className={s.productImages_tag} style={{ backgroundColor: product.product_tags[t].listingColor || '#FFF', color: blackOrWhite(product.product_tags[t].listingColor || '#FFF', 1.05) }}>{ product.product_tags[t].tag }</span>;
		});
	}

	if (!soldOut && price.newProduct && !newSet) {
		badges.unshift(<span key="new-badge" className={[s.productItem_badge, s.productItem_badge__text].join(' ')}>{ translate(setup, 'new') }</span>);
	} else if (!soldOut && price.showAsOnSale && !saleSet) {
		badges.unshift(<span key="sale-badge" className={[s.productItem_badge, s.productItem_badge__text].join(' ')}>{ `-${salePercent}` }</span>);
	}

	let productMedia = deepClone(product.media || []);
	if (product.film && Object.keys(product.film).length > 0) {
		const productFilm = Object.keys(product.film)
			.filter((f) => {
				if (product.film[f].incPricelist && product.film[f].incPricelist.split(/\s*,\s*/).indexOf(setup.pricelist) === -1) return false;
				if (product.film[f].incMarket && product.film[f].incMarket.split(/\s*,\s*/).indexOf(String(setup.market)) === -1) return false;
				if (product.film[f].incCountry && product.film[f].incCountry.split(/\s*,\s*/).indexOf(setup.country) === -1) return false;
				return true;
			})
			.map((key) => product.film[key])
			.sort((a, b) => (parseInt((a.order || 0), 10) >= parseInt((b.order || 0), 10) ? 1 : -1));

		productFilm.forEach((film) => {
			productMedia = [...productMedia.slice(0, parseInt((film.order || 0), 10)), { ...film }, ...productMedia.slice(parseInt((film.order || 0), 10))];
		});
	}

	const features = (product.features && Object.keys(product.features)
		.filter((f) => {
			if (product.features[f].incPricelist && product.features[f].incPricelist.split(/\s*,\s*/).indexOf(setup.pricelist) === -1) return false;
			if (product.features[f].incMarket && product.features[f].incMarket.split(/\s*,\s*/).indexOf(String(setup.market)) === -1) return false;
			if (product.features[f].incCountry && product.features[f].incCountry.split(/\s*,\s*/).indexOf(setup.country) === -1) return false;
			return true;
		})
		.reduce((acc, item) => {
			const f = product.features[item];
			if (!acc[f.featureId]) acc[f.featureId] = [];
			return { ...acc, [f.featureId]: [...acc[f.featureId], f] };
		}, {})) || {};

	const attributes = ((product.product_attributes && Object.keys(product.product_attributes)
		.filter((a) => {
			if (parseInt(product.product_attributes[a].is_new, 10) && !price.newProduct) return false;
			if (parseInt(product.product_attributes[a].is_sale, 10) && !price.showAsOnSale) return false;
			if (product.product_attributes[a].incPricelist && product.product_attributes[a].incPricelist.split(/\s*,\s*/).indexOf(setup.pricelist) === -1) return false;
			if (product.product_attributes[a].incMarket && product.product_attributes[a].incMarket.split(/\s*,\s*/).indexOf(String(setup.market)) === -1) return false;
			if (product.product_attributes[a].incCountry && product.product_attributes[a].incCountry.split(/\s*,\s*/).indexOf(setup.country) === -1) return false;
			return true;
		})) || []).map((a) => product.product_attributes[a]);

	const locallyConfig = {
		company_name: 'Marsblade',
		button_text: 'Find it Locally',
		button_id: 'HTML',
		company_id: '186009',
		css: '4',
		always_hide_button: '1',
		style: product.sku,
		show_location_switcher: '1',
		show_location_prompt: '1',
		lang: 'en-us',
		show_dealers: '1',
		n_dealers: '10',
		target: 'new',
		category: 'MP',
		show_address: '1',
		radius_in_km: '10000',
		only_show_country: setup.country,
	};


	/*
	  var lcly_config_0 = {
	    "company_name": "Marsblade",
	    "button_id": "HTML",
	    "company_id": "186009",
	    "css": "4",
	    "always_hide_button": "1",
	    "style": "F01-7031",
	    "upc": "850032422628",
	    "show_product_image": "1",
	    "show_brand_name": "1",
	    "show_product_name": "1",
	    "show_location_switcher": "1",
	    "show_location_prompt": "1",
	    "lang": "en-us",
	    "show_dealers": "1",
	    "n_dealers": "3",
	    "show_only_stocking_dealers": "1"
	  };
	*/

	const locallyQuery = Object.keys(locallyConfig).reduce((a, k) => {
		a.push(`${encodeURIComponent(k)}=${encodeURIComponent((typeof locallyConfig[k] === 'object' && locallyConfig[k] !== null) ? JSON.stringify(locallyConfig[k]) : locallyConfig[k])}`);
		return a;
	}, []).join('&');
	const locallyEndpoint = `https://marsblade.locally.com/stores/map.js?${locallyQuery}`;

	const mountingFeature = useRef(null);
	useEffect(() => {
		if (mountingFeature.current && mountingFeature.current.querySelector('#lcly-button-0')) addScript(locallyEndpoint, 'lcly-script-0').catch(() => { /* fail silently */ });
		return (() => {
			if (mountingFeature.current && mountingFeature.current.querySelector('#lcly-button-0')) {
				removeScript(locallyEndpoint, 'lcly-script-0');
				mountingFeature.current.querySelector('#lcly-button-0').innerHTML = '<a id="lcly-link-0" data-switchlive="true" data-switchlive-mode="auto" data-switchlive-id-pl="6" role="button" href="https://join.locally.com/for-brands" target="_blank" rel="noreferrer">Product Locator by Locally</a>';
			}
		});
	}, [product, setup]);

	return (
		<main role="main">
			<Top setup={setup} pageContent={metaInfo} paths={data.paths} skipGeneral />
			<div id="productTop" className={s.product}>
				<ErrorBoundary message="Product images missing data" debugData={product}>
					<div className={s.productImages}>
						{
							tags.length > 0 && (
								<div className={s.productImages_tags}>
									{ tags }
								</div>
							)
						}
						{
							productMedia.length > 0 && (
								<Slider adaption="product" infinite showArrows={false} showNav={false} showCounter={false} thumbs>
									{
										productMedia.map((media, i) => {
											if (media.src) {
												return (
													<a key={media.src.url} className={s.productImages_film} href="#toggle-sound" onClick={toggleSoundHandler} data-film>
														<div className={[s.productImages_media, s.productImages_media__film].join(' ')}>
															<VideoPlayer src={media.src.url} loop playsInline muted poster={media?.poster?.url} />
														</div>
													</a>
												);
											}
											return (
												<div key={media.url} className={s.productImages_media}>
													<LazyImage {...imageSizeTranslation('10', media, (i === 0 ? 'eager' : true))} className={[s.productImages_media_image]} alt={`${product.brandName} ${product.name}`} />
												</div>
											);
										})
									}
								</Slider>
							)
						}
					</div>
				</ErrorBoundary>
				<div className={s.productDetail}>
					<Breadcrumbs currentPath={rootUrl(product.canonicalUri)} filter={filter} filtering={productCache[setup.market][setup.pricelist]} data={data} setup={setup} product={product} resetFilter={props.resetFilter} />
					<ErrorBoundary message="Product details missing data" debugData={product}>
						{ badges }
						<div className={s.productDetail_top}>
							<h1 className={s.productDetail_top_title}>{product.name}</h1>
						</div>
						<div className={s.productDetail_info}>
							<div className={s.productDetail_info_top} dangerouslySetInnerHTML={{ __html: purifier(descriptionIntro) }} />
							<a className={s.productDetail_info_link} href="#product-info"><span>{ translate(setup, 'productDetailsReadMore') }</span></a>
						</div>

						<div className={s.productDetail_purchase}>
							{ sizes.length > 0 && <Purchase sizes={sizes} setup={setup} currentPath={currentPath} productId={product.product} relatedProducts={productRelations} soldOut={soldOut ? setup.statics.soldOut : false} currentVariant={currentVariant} switchVariant={switchVariant} tydan={props.tydan} /> }
						</div>
					</ErrorBoundary>
					<ErrorBoundary message="Product attributes missing data" debugData={product.product_attributes}>
						{
							attributes.length > 0 && (
								<div className={s.productDetail_attributes}>
									{
										attributes.map((attr) => (
											<div key={attr.content} className={s.productDetail_attribute}>
												<svg className={s.productDetail_attribute_icon} xmlns="http://www.w3.org/2000/svg" width="19" height="15" viewBox="0 0 19 15">
													<polyline fill="none" stroke="#2B4B9B" strokeLinecap="square" strokeWidth="3" points="751 759.143 754.9 763 764 754" transform="translate(-748 -751)" />
												</svg>
												<p className={s.productDetail_attribute_text} dangerouslySetInnerHTML={{ __html: purifier(attr.content) }} />
											</div>
										))
									}
								</div>
							)
						}
					</ErrorBoundary>
					{
						features.mounting && features.mounting.map((mounting) => {
							const { feature = '', description = '', image = {} } = mounting;
							return (
								<div key={feature} className={s.mounting} ref={mountingFeature}>
									{
										description !== 'locally' && (
											<>
												{ image.url && <img className={s.mounting_icon} src={image.url} width="60" height="60" alt={`${feature} icon`} /> }
												<h5 className={s.mounting_title}>{ feature }</h5>
											</>
										)
									}
									{
										description === 'locally' && (
											<div className={s.mounting_locally}>
												<div className={s.mounting_locally_section}>
													{ image.url && <img className={s.mounting_icon} src={image.url} width="60" height="60" alt={`${feature} icon`} /> }
													<div className={s.mounting_locally_section_content}>
														<strong>{ feature }</strong>
														{ translate(setup, 'configuratorShowLocally') }
													</div>
												</div>
												<div className={s.mounting_locally_content}>
													<div id="lcly-button-0" data-switchlive="true" data-switchlive-impression="true" data-switchlive-impression-id-pl="1">
														<a id="lcly-link-0" data-switchlive="true" data-switchlive-mode="auto" data-switchlive-id-pl="6" role="button" href="https://join.locally.com/for-brands" target="_blank" rel="noreferrer">Product Locator by Locally</a>
													</div>
												</div>
											</div>
										)
									}
								</div>
							);
						})
					}
				</div>
			</div>
			<div id="product-info" className={s.productInfo}>
				<div className={s.productInfo_sections}>
					{
						descriptionParts.map((p) => {
							const sectionParts = p.split('</strong>');
							const sectionTitle = sectionParts.length > 1 ? sectionParts[0] : translate(setup, 'productInfoDescription');
							const sectionContent = (sectionParts.length > 1 ? sectionParts[1] : sectionParts[0]).replace(/^(<br\s?\/?>)*/, '').replace(/(<br\s?\/?>\s*)*$/, '');
							return (
								<div className={s.productInfo_section}>
									<h6 className={s.productInfo_section_title}>{ sectionTitle }</h6>
									<div className={s.productInfo_section_content} dangerouslySetInnerHTML={{ __html: purifier(sectionContent) }} />
								</div>
							);
						})
					}
				</div>
			</div>
			{
				productSections.length > 0 && (
					<div className={s.productCMS}>
						<ErrorBoundary message="Product content incorrect format" debugData={productSections}>
							{
								productSections.map((section) => (
									<TemplateParser sections={section.sections} setup={setup} product={product} isProduct={product.product} />
								))
							}
						</ErrorBoundary>
					</div>
				)
			}
			{
				productRelations?.standard ? (
					<ErrorBoundary message="Related Product details missing data" debugData={productRelations}>
						<div className={s.productRelations}>
							<h3 className={s.productRelations_title}>{ translate(setup, 'relatedProducts') }</h3>
							<div className={s.productRelations_inner}>
								<Pagination ListComponent={ProductItem} list={productRelations?.standard} cache={cache} currentPath="relatedProducts" canonical productPicker />
							</div>
						</div>
					</ErrorBoundary>
				) : null
			}
		</main>
	);
};

const mapState = ({ environment, productCache, checkout, filter, pageData }, { data, currentPath }) => ({
	data,
	currentPath,
	header: environment.header,
	tydan: environment.tydan,
	setup: environment.setup,
	ignoreSizeRelations: environment?.getParameters?.singleSize || false,
	productCache,
	listItemCache: checkout.listItemCache,
	filter,
	pageData,
});

export default hot(module)(connect(mapState, { resetFilter })(Product));
